import { FC } from 'react';
import withContainer from 'components/Container/withContainer';
import { withAuth } from 'context/Auth';
import { PowerTable } from 'ui/PowerTable';
import {
    getReport,
    createReportFile,
    downloadAssignsReport
} from 'api/reports/reports';
import { ColumnProps, TableFilters } from 'ui/Table';
import { ReportFilters, ReportRow } from 'api/reports/reports.types';
import styled, { css } from 'styled-components';
import { TextXS } from 'ui/Styled/Styled';
import { themeable } from 'themes/utils';
import { getTranslateGroup } from 'messages';
import { UserType, OrganizationType } from 'api/api';
import { QueryKey } from 'helpers/constants';
import { getAllServiceStudents } from 'api/students/students';
import { getCoursesOptions } from 'api/courses/courses';
import { PowerTableGetterProps } from 'ui/PowerTable/PowerTable.types';
import { Button } from 'ui/Button';
import { getAllServiceOrganizations } from 'api/organizations/organizations';
import { RouterPaths } from 'helpers/router-paths';
import { DocumentType } from 'api/api';
import { formatMoney } from 'utils/intl';

const DocumentsRow = styled.div`
    display: flex;
    flex-flow: row;
    align-items: center;
    justify-content: center;
    gap: 4px;
`;

const DocumentAbbr = styled(TextXS)<SActiveProps>`
    padding: 4px 6px;
    border-radius: 6px;
    border: 1px solid ${themeable('primaryColor')};

    ${({ $active }) =>
        $active
            ? css`
                  background: ${themeable('primaryColor')};
              `
            : css`
                  color: ${themeable('secondaryColor')};
              `}
`;

const documentAbbrs = getTranslateGroup('documentsAbbrs');
const documentTitles = getTranslateGroup('issuedDocuments');

const REPORT_COLUMNS: ColumnProps<ReportRow> = {
    dateEndLearning: {
        title: 'Дата',
        type: 'date',
        width: 150
    },
    organizationTitle: {
        title: 'Организация'
    },
    fullNameStudent: {
        title: 'Студент'
    },
    courseTitle: {
        title: 'Курс'
    },
    recipiencyDocumentsType: {
        title: 'Выдача',
        width: 200,
        translateGroup: 'recipiencyDocumentsType'
    },
    documents: {
        title: 'Документы',
        width: 300,
        parse: (value) => {
            return (
                value && (
                    <DocumentsRow>
                        {Object.entries(documentTitles).map(
                            ([rawKey, title]) => {
                                const key = rawKey as DocumentType;

                                return (
                                    <DocumentAbbr
                                        key={key}
                                        $active={value.includes(key)}
                                        title={title}
                                    >
                                        {documentAbbrs[key]}
                                    </DocumentAbbr>
                                );
                            }
                        )}
                    </DocumentsRow>
                )
            );
        }
    },
    issueStatus: {
        title: 'Статус',
        width: 120,
        parse: (value) => (value ? 'Выдан' : 'Не выдан')
    },
    price: {
        title: 'Сумма',
        width: 150,
        parse: formatMoney
    },
    educationStudentsCount: {
        title: (
            <>
                Кол-во
                <br />
                студентов
            </>
        ),
        width: 120,
        parse: (value) => (value ? value : '0')
    },
    balanceOrganization: {
        title: (
            <>
                Баланс
                <br />
                организации
            </>
        ),
        width: 150,
        parse: formatMoney
    }
};

const filters: TableFilters<ReportRow, ReportFilters> = {
    period: {
        column: 'dateEndLearning',
        component: 'date'
    },
    organizations: {
        column: 'organizationTitle',
        component: 'select',
        props: {
            queryKey: [QueryKey.Organization],
            onSearch: getAllServiceOrganizations,
            multiple: true
        }
    },
    students: {
        column: 'fullNameStudent',
        component: 'select',
        props: {
            queryKey: [QueryKey.Student],
            onSearch: getAllServiceStudents,
            multiple: true
        }
    },
    courses: {
        column: 'courseTitle',
        component: 'select',
        props: {
            queryKey: [QueryKey.Course],
            onSearch: getCoursesOptions,
            multiple: true
        }
    },
    issueStatus: {
        column: 'issueStatus',
        component: 'select',
        props: {
            hideInput: true,
            options: [
                { label: 'Выдан', value: true },
                { label: 'Не выдан', value: false }
            ]
        }
    }
};

const rightContent = (props: PowerTableGetterProps<ReportFilters>) => {
    return (
        <Button icon="download" onClick={createReportFile.bind(this, props)}>
            Скачать
        </Button>
    );
};

const Report: FC = () => {
    return (
        <PowerTable
            heading="Отчет по бланкам"
            columns={REPORT_COLUMNS}
            getter={getReport}
            linkGetter={RouterPaths.CourseAssignment}
            filters={filters}
            headingRightContent={rightContent}
            onDownload={downloadAssignsReport}
            enabledColumn="hasGeneratedDocs"
            disabledHint="Документы для этого назначения не зафиксированы"
            pagination
        />
    );
};

export default withAuth(
    withContainer(Report, {
        hideHeading: true,
        flex: true
    }),
    undefined,
    UserType.Operator,
    OrganizationType.EducationCenter
);
